/**
 * Backend decision -> the six internal states -> the OWNER's five headings.
 *
 * Since 2026-09-04 the two mappings are separate, and that is the point (UX_SPEC, задача #11;
 * (правила текстов — ТЗ.md). A decision still becomes exactly one `VerdictKind`; the heading
 * is then chosen by the PAIR «состояние × тип поля» in `verdictHeading`, because on a meadow
 * a standing sward and a loss of vegetation carry the same heading — mowing does not stop the
 * field being a meadow. A `Record<VerdictKind, string>` could not express that, and the six
 * headings it held are gone from the dictionary rather than kept beside the five.
 *
 *   FIELD_RELEASED / HARVEST_DETECTED           -> free
 *   FIELD_RELEASED_BY_RADAR                     -> radar
 *   FIELD_MAYBE_RELEASED / HARVEST_LIKELY       -> maybe
 *   FIELD_UNDER_CROP                            -> crop
 *   FIELD_BARE                                  -> fallow
 *   NO_DATA / HARVEST_NOT_CONFIRMED             -> nodata (always with a reason)
 *   failed run                                  -> nodata, heading `run.failed` (reason by code)
 *
 * BOTH VOCABULARIES ARE MAPPED. The FIELD_* family is what production writes
 * (`SCORING_MODEL = final`); the HARVEST_* one survives in older rows. Until 2026-08-02
 * only the second was listed here, so every shipped verdict fell off the end of the map
 * and the headline rendered EMPTY on screen.
 *
 * ПОЛЕ ЗЕЛЁНОЕ is a fourth state and not a flavour of silence: a standing crop at NDVI
 * 0.8 and a window with one readable image are opposite situations, and printing the same
 * words for both told the user to do the same thing in both. It is still not a negative
 * claim — the pipeline cannot support "поле не убрано" and there is not one verified
 * negative field (removed research docs (git history)) — it is what the images show, said as an
 * observation.
 *
 * `score_0_100` is carried through untouched but is no longer PRINTED on the product
 * surface. It equals the signal step divided by its own trigger, so showing the number
 * shows the threshold; and no word for it survived first reading — a bare 0-100 reads as
 * per cent, and anything built on "уверенность" would promise a probability that was never
 * measured (13 of 18 held out, Wilson [0.49, 0.88], zero verified negative fields). The
 * number lives on the developer surface and in the operator's reveal; the shape of the drop,
 * said in words, lives in the "Что видно на снимках" disclosure (`dropLine`).
 */

import { T } from "../strings";
import type { AuditRow, Decision, FieldUseCode, Obstruction, Run } from "../api/types";
import {
  DISPLAY_SHARP_DROP_SCORE,
  DISPLAY_SNOW_DOMINANT_FRACTION,
  PIPELINE_OPTICAL_STALE_DAYS,
} from "./constants";
import { formatDayMonth, formatDayMonthSpan, isFiniteNumber } from "./format";
import { surfaceZoneOf, type SurfaceZone } from "./vegetation";

export type VerdictKind = "free" | "radar" | "maybe" | "crop" | "fallow" | "nodata";

/** How many dates of the window the QA gate let through, out of how many it saw. */
export interface VerdictQa {
  accepted: number;
  total: number;
}

export interface Verdict {
  kind: VerdictKind;
  /** Прогон УПАЛ. Отдельно от `kind`, потому что заголовок у отказа свой (`run.failed`). */
  failed: boolean;
  /**
   * Заголовок владельца: выбран парой «состояние × тип поля», а у схода — ещё и зоной.
   * Не одним состоянием.
   */
  label: string;
  /**
   * ЗОНА ПОВЕРХНОСТИ последнего чистого снимка — считана ОДИН раз на прогон и разошлась
   * отсюда всем, кто её спрашивает: заголовок, тон и оговорка обязаны читать одно и то же
   * число, иначе карточка скажет «пахота» жёлтым или «стерня» под оговоркой про пахоту.
   */
  zone: SurfaceZone;
  /**
   * Светофор: можно копать, нельзя, между ними или неизвестно. Согласован с `label`, а внутри
   * разрешающих заголовков выбран ПОВЕРХНОСТЬЮ поля, а не уверенностью в дате.
   */
  tone: VerdictTone;
  /** Цвет тона, а не состояния: `var(--verdict-go|caution|stop|unknown)`. */
  colorVar: string;
  score: number | null;
  /** Тезис: одна фраза о ПОЛЕ, на которую опираются, принимая решение ехать. */
  human: string;
  /**
   * Строка 4 — то, что делает тезис утверждением: сухость, оговорка радара, «что было после
   * этой даты», измерение под «ПОЛЕ ГОЛОЕ», приглашение вернуться. Одна на состояние.
   */
  secondLine: string | null;
  /**
   * ПРИГЛАШЕНИЕ ВЕРНУТЬСЯ — свой слот, а не строка 4, и стоит оно ровно у «Нет чётких
   * снимков». Правило одного отрицания (UX_SPEC §3.2) им не задето: о том, ЧЕГО НЕТ, говорит
   * одна строка — причина; приглашение говорит о другом — когда смотреть снова, — и потому
   * это совет, а не второй голос. Разное по причине: снег сходит неделями, облако — за день.
   * У упавшего прогона его нет: там ждать нечего и не погоды.
   */
  comeBack: string | null;
  /**
   * ХАРАКТЕР СХОДА — резкий или постепенный. С 2026-09-05 живёт в раскрытии «Что видно на
   * снимках», а НЕ строкой карточки (drop-lines-off-card): семейство `changeLine`
   * (`changeNotApplicable` / `changeUnknown*`) удалено из словаря — тройной дубль темы
   * «даты нет» и мёртвая ветка nodata. Не-null только у `free`; граница между двумя
   * формулировками (`DISPLAY_SHARP_DROP_SCORE`) — только слова, ни одно решение от неё
   * не зависит.
   */
  dropLine: string | null;
  /**
   * Поверхность НЕ ПРОЧИТАНА, и жёлтый выпал именно поэтому — только тогда. Тон схода берётся
   * от зоны последнего чистого снимка, а называет её словом полоска уровня; когда зоны нет,
   * полоски тоже нет, и «между» осталось бы без единой видимой строки под ним. Во всех прочих
   * случаях здесь `null`: повторять то, что уже сказано в доказательстве, — второй голос.
   */
  surfaceLine: string | null;
  /** Тело раскрытия «Чем измерено»; null там, где измерять было нечего. */
  measured: string | null;
  /** Mandatory for `nodata`, null otherwise. */
  reason: string | null;
  eventFrom: string | null;
  eventTo: string | null;
  dryLine: string | null;
  /** Свежайшая чистая дата прогона — она же дата снимка, который зовут посмотреть. */
  latestClearDate: string | null;
  /** Код препятствия: приглашение вернуться разное у снега, облаков и «не снимали». */
  obstructionCode: Obstruction["code"] | null;
  /** Null under the notebook models, which never recorded the gate's counts. */
  qa: VerdictQa | null;
  warnings: string[];
  needsEyes: boolean;
  /** Set only for a radar answer: what optics alone said, in the product's own words. */
  opticalAnswer: string | null;
  runId: number;
  analysisDate: string;
}

const KIND_BY_DECISION: Record<Decision, VerdictKind> = {
  HARVEST_DETECTED: "free",
  HARVEST_LIKELY: "maybe",
  HARVEST_NOT_CONFIRMED: "nodata",
  FIELD_RELEASED: "free",
  // Its own kind, never folded into "free". They differ in the sensor, in the width of the
  // answer and in what is known about either — see the note at the top of this file.
  FIELD_RELEASED_BY_RADAR: "radar",
  FIELD_MAYBE_RELEASED: "maybe",
  FIELD_UNDER_CROP: "crop",
  // Its own kind, and deliberately neither "crop" nor "nodata". It is the opposite of the
  // first and the negation of the second: twelve clear images at full coverage showing soil
  // is not an absence of data. Nothing was harvested here, because nothing grew — but the
  // question the card answers is whether the field can be dug, and clean fallow can, so
  // since 2026-09-04 it has its own heading («Поле не сеяно (пары)») and, on open soil or
  // with no reading at all, the `go` tone.
  FIELD_BARE: "fallow",
  NO_DATA: "nodata",
};

/** Decision -> one of the four allowed states, without a run around it. */
export const verdictKindOfDecision = (decision: Decision | null | undefined): VerdictKind =>
  decision ? (KIND_BY_DECISION[decision] ?? "nodata") : "nodata";

/**
 * ЕДИНСТВЕННОЕ УСЛОВИЕ, ПРИ КОТОРОМ ИСТОРИЯ ПОЛЯ УСТУПАЕТ СВЕЖЕМУ СНИМКУ: сход растительности
 * ВИДЕЛИ и земля при этом ОТКРЫТА.
 *
 * Написано один раз и зовётся из трёх мест (заголовок, тон, оговорка) именно потому, что
 * разойтись они не имеют права: тон — цвет заголовка, а оговорка — его цена. Три копии одного
 * условия и есть механизм, которым они расходятся.
 */
const canopyGoneOnBareSoil = (kind: VerdictKind, zone: SurfaceZone): boolean =>
  zone === "bare" && (kind === "free" || kind === "radar" || kind === "maybe");

/**
 * ШЕСТЬ заголовков владельца на шесть внутренних состояний, и совпадение это случайное:
 * выбирает их не одно состояние, а ПАРА «состояние × тип поля», а у схода растительности —
 * ещё и ЗОНА последнего чистого снимка (правила текстов — ТЗ.md). Прежняя карта
 * `VERDICT_LABEL` — заголовок по одному `VerdictKind` — не могла выразить ни того, ни другого:
 * на лугу стоящая трава и сход дают ОДИН заголовок, потому что укос не перестаёт быть лугом, а
 * на пашне один и тот же сход даёт РАЗНЫЕ заголовки в зависимости от того, что осталось на
 * земле.
 *
 * `mixed` и `unknown` ведут себя как пашня: смешанное поле — это незнание, какое из двух, а
 * поправку к заголовку несёт оговорка (`caveat.unknownUse` под №2, `caveat.unknownUseTilled`
 * под №3), а не второй заголовок.
 */
export const verdictHeading = (
  kind: VerdictKind,
  family: FieldUseFamily,
  zone: SurfaceZone,
): string => {
  // Пар и отсутствие снимков не смотрят ни на тип поля, ни на зону: чистый пар выглядит
  // одинаково и на пашне, и на лугу, а закрытое небо — это про съёмку, а не про поле.
  if (kind === "fallow") return T.verdict.heading.notSown;
  if (kind === "nodata") return T.verdict.heading.noShots;
  // ТРАВА ПЕРЕХВАТЫВАЕТ ЗАГОЛОВОК, НО НЕ ПРОТИВ СВЕЖЕГО СНИМКА. Перехват был безусловным, и
  // на поле, где травы стояли три года, а этим летом растительность сошла до открытой почвы,
  // карточка продолжала говорить «копать запрещено»: тип поля — история, а зона — сегодня, и
  // история пересиливала наблюдение. Это шаги 2–3 порядка агронома в обратном порядке.
  //
  // Условие узкое НАРОЧНО: сход ВИДЕЛИ (free/radar/maybe) и земля ОТКРЫТА. Пока трава стоит
  // (crop), пока зона — растительность или не прочитана, пока состояния нет вовсе — перехват
  // жив, потому что укос лугом быть не перестаёт. Снимается он ровно там, где история и
  // снимок говорят разное, а дороже ошибиться в сторону истории: на открытой почве человек
  // хотя бы приедет и увидит сам, а под запретом он не приедет никогда.
  if (family === "grass" && !canopyGoneOnBareSoil(kind, zone))
    return T.verdict.heading.perennialGrass;
  if (kind === "crop") return T.verdict.heading.cropSown;
  // №2 и №3 — ОДИН И ТОТ ЖЕ сход растительности, разведённый зоной последнего чистого снимка,
  // и различает их ровно одна величина: сколько растительности на поле осталось. Вида земли
  // снимок не показывает ни в одном канале — борозды в №3 никто не видел, — поэтому оговорка
  // `caveat.tilled` под ним обязательна и говорит именно это.
  if (zone === "bare") return T.verdict.heading.tilled;
  // ХВОСТ «— НА ЗЕМЛЕ СТЕРНЯ» — ТОЛЬКО ТАМ, ГДЕ СТЕРНЯ ПРОЧИТАНА. Он печатался при любой
  // зоне, кроме непрочитанной, то есть и при зоне «растительность»: заголовок утверждал
  // стерню, а полоска уровня по ТОМУ ЖЕ снимку говорила обратное — два голоса об одной
  // поверхности, и один из них заведомо неверный. Утверждение о поверхности держится только
  // на зоне, которая его подтверждает; при зелени и при непрочитанной зоне остаётся «Поле
  // убрано» без хвоста. О самой поверхности там, где её нет, говорит `surfaceUnknown` — и
  // только там: при зелени зона прочитана, и о ней говорит полоска.
  return zone === "stubble" ? T.verdict.heading.harvested : T.verdict.heading.harvestedNoSurface;
};

/**
 * СВЕТОФОР КАРТОЧКИ — решение владельца (2026-09-04): красный «копать нельзя», зелёный
 * «можно», между ними — жёлтый.
 *
 * Тон отвечает на ЕДИНСТВЕННЫЙ вопрос, ради которого карточку открывают, и потому он взят не
 * от `kind`, а от пары «состояние × тип поля» — от того же, от чего заголовок, — плюс от
 * ПОВЕРХНОСТИ поля там, где заголовок копать не запрещает (см. `verdictTone`). Прежняя карта
 * `VERDICT_COLOR_VAR` красила по одному `kind`, и «уборка» выходила то охряной (оптика), то
 * сланцевой (радар, «возможно») — три цвета под одним и тем же заголовком, без объяснения, а
 * на лугу зелёный цвет вегетации стоял под заголовком «копать запрещено».
 */
export type VerdictTone = "go" | "caution" | "stop" | "unknown";

/**
 * ТОН БЕРЁТСЯ ОТ ПОВЕРХНОСТИ ПОЛЯ, А НЕ ОТ УВЕРЕННОСТИ В ДАТЕ (решение владельца, 2026-09-04).
 *
 * По стерне не копают — копают по открытой почве: пахота, дискование, пар. До этой правки
 * зелёный получал `free` и только он, то есть зелёным был ОПТИЧЕСКИЙ СХОД, а не проходимость;
 * поле, с которого зерно убрали неделю назад и которое стоит в стерне до самой весны,
 * приглашало ехать. Уверенность в дате при этом никуда не делась — она осталась текстом
 * (строка 4 радара, «Дату определяет только осмотр снимка»), но цветом больше не говорит:
 * тон и текст отвечали на разные вопросы, а читались как один.
 *
 * ВЕТВЛЕНИЕ ПО-ПРЕЖНЕМУ ПОВТОРЯЕТ `verdictHeading` СТУПЕНЬ В СТУПЕНЬ, и это обязательное
 * условие: тон — цвет заголовка, разойтись с ним он не может. Заголовок «копать запрещено» —
 * всегда `stop`, какое бы состояние и какая бы поверхность его ни сопровождали.
 *
 * `zone` — класс поверхности последнего чистого снимка, тот же самый, что полоска уровня
 * называет словом (`surfaceZoneOf`). Никаких своих порогов здесь нет и быть не может.
 */
export const verdictTone = (
  kind: VerdictKind,
  family: FieldUseFamily,
  zone: SurfaceZone,
): VerdictTone => {
  // `notSown`: пар на тип поля не смотрит — как и заголовок, — но на поверхность смотрит, и
  // почти всегда она открытая. БЕЗ ЗОНЫ пар остаётся зелёным, в отличие от уборки: за ним
  // стоит сезон чистых снимков без растительности с границей Уилсона, а не один сход, после
  // которого на земле осталась стерня.
  if (kind === "fallow") return zone === "bare" || zone === "unknown" ? "go" : "caution";
  // `noShots`: ответа нет. Незнание — не запрет, и красным оно не красится: красный занят
  // «нельзя», и отдать его отказу значит сказать «нельзя» там, где мы просто не смотрели.
  if (kind === "nodata") return "unknown";
  // `perennialGrass`: на лугу сход растительности — укос, копать всё равно нельзя. Поэтому
  // `stop` стоит ВЫШЕ разбора состояния: любой kind на лугу даёт запрет, кроме двух над ним.
  //
  // СХОД ДО ОТКРЫТОЙ ПОЧВЫ — ЕДИНСТВЕННОЕ ИСКЛЮЧЕНИЕ, И ОНО ЖЁЛТОЕ, А НЕ ЗЕЛЁНОЕ (арбитраж
  // лида 2026-10-09). Заголовок там уже не травяной — его выбрала зона (`verdictHeading`), —
  // но и приглашать ехать нельзя: за зелёным на пашне стоит ОЖИДАЕМЫЙ сход, а здесь три года
  // истории спорят с одним снимком. Ложное «ехать» у продукта дороже пропуска: проверенных
  // отрицательных у нас ноль. Поэтому зелёного на травяном семействе не бывает ни при одном
  // состоянии схода — ступень по зоне пашни здесь НЕ повторяется, и это сказано вслух, чтобы
  // следующий читатель не «починил» расхождение с `verdictHeading` обратно.
  if (family === "grass") return canopyGoneOnBareSoil(kind, zone) ? "caution" : "stop";
  // `cropSown`.
  if (kind === "crop") return "stop";
  // СМЕШАННОЕ ПОЛЕ НА СХОДЕ — ЖЁЛТОЕ ПО ТОЙ ЖЕ ПРИЧИНЕ, ЧТО ЛУГОВОЕ (решение владельца
  // 2026-10-09: «по пашне/лугу сделай как агроном рекомендует»). Заголовок там остаётся №3 —
  // смешанное поле ведёт себя как пашня, и поправку несёт оговорка `unknownUseTilled`, — но
  // зелёного быть не может: КАКАЯ ИМЕННО половина поля попала в зону, снимок не говорит, а
  // если луговая, то зелёный зовёт ехать туда, где копать нельзя.
  //
  // ЧИСТЫЙ `unknown` ЭТИМ НЕ ЗАТРОНУТ, и это не недосмотр: «тип не посчитан» — это поле,
  // которое вполне может быть целиком пашней, и оговорка там говорит именно это. У `mixed`
  // же известно, что луговая половина ЕСТЬ.
  if (family === "mixed" && canopyGoneOnBareSoil(kind, zone)) return "caution";
  // `harvested` — все три состояния схода разом, оптическое оно или радарное. Зелёный даёт
  // только открытая почва: стерня — «сход был, но копать по ней не станут, ждать обработки»;
  // растительность под этим заголовком странна сама по себе (отава? всходы?); а зоны нет —
  // значит, обещать зелёное вслепую нечем.
  return zone === "bare" ? "go" : "caution";
};

export const VERDICT_TONE_COLOR_VAR: Record<VerdictTone, string> = {
  go: "var(--verdict-go)",
  caution: "var(--verdict-caution)",
  stop: "var(--verdict-stop)",
  unknown: "var(--verdict-unknown)",
};

/**
 * Тип поля, огрублённый до того, что меняет ТРАКТОВКУ схода растительности. «Пашня» и
 * «Скорее пашня» дают одно и то же перечисление причин: разница между ними — уверенность
 * ячейки 1, а не набор того, что могло случиться на поле.
 */
export type FieldUseFamily = "arable" | "grass" | "mixed" | "unknown";

const USE_FAMILY: Record<FieldUseCode, FieldUseFamily> = {
  PREDOMINANTLY_ARABLE: "arable",
  POSSIBLY_ARABLE: "arable",
  PREDOMINANTLY_PERENNIAL_GRASS: "grass",
  POSSIBLY_PERENNIAL_GRASS: "grass",
  MIXED_FIELD_USE: "mixed",
  UNCERTAIN: "unknown",
};

export const fieldUseFamily = (code: FieldUseCode | null | undefined): FieldUseFamily =>
  code ? (USE_FAMILY[code] ?? "unknown") : "unknown";

/**
 * ОГОВОРКА — цена заголовка, и на экране она стоит ВСЕГДА, не в раскрытии
 * (правила текстов — ТЗ.md). Прежде этот слот назывался трактовкой и отвечал на «что из
 * этого следует» при заголовке, который говорил только про измеренное; теперь трактовка
 * переехала в сам заголовок, а слот держит единственное, чем заголовок снят: перечисление
 * причин, между которыми снимок не выбирает. Правило прежнее — причин НЕСКОЛЬКО.
 *
 * У «На поле посеяна культура» оговорки нет: её работу делает `secondLine`
 * («Что было после этой даты, мы не знаем»). У «Нет чётких снимков спутника» оговаривать
 * нечего, и выдумывать содержание молчания запрещено.
 */
export const verdictCaveat = (
  kind: VerdictKind,
  use: FieldUseCode | null | undefined,
  zone: SurfaceZone,
): string | null => {
  if (kind === "fallow") return T.verdict.caveat.fallow;
  if (kind !== "free" && kind !== "radar" && kind !== "maybe") return null;
  const family = fieldUseFamily(use);
  // Оговорка идёт ЗА ЗАГОЛОВКОМ, ступень в ступень: у №3 своя цена, и она тяжелее — заголовок
  // называет работу техники, которой ни один канал не видит.
  //
  // У `mixed` и `unknown` зона спрашивается ТОЖЕ, и с 2026-09-05 у пары «тип не посчитан ×
  // открытая почва» своя строка (caveat-unknownuse-tilled). Слот один, а поправок под №3 две
  // — «может, это луг» и «пахота прочитана по уровню, а не по земле», — и общая оговорка
  // умещала только вторую: под заголовком, который утверждает пахоту, она начиналась
  // перечислением неразличимых причин, то есть спорила с ним первым же словом. Обе поправки
  // теперь стоят в одной строке и в своём порядке — сперва основание заголовка, потом тип
  // поля.
  //
  // У ТРАВЫ ПОД ЗАГОЛОВКОМ №3 — СВОЯ ОГОВОРКА, и она ЗАМЕНЯЕТ `tilled`, а не дополняет его:
  // слот один. Случай тот самый, где перехват снят (`canopyGoneOnBareSoil`), и поправка там
  // нужна тяжелее прочих — заголовок говорит про обработку почвы на поле, которое три года
  // числилось луговым. Общая `grass` здесь не годится: она перечисляет причины схода, но
  // молчит о главном — что история поля спорит с этим снимком.
  const base =
    family === "arable"
      ? zone === "bare"
        ? T.verdict.caveat.tilled
        : T.verdict.caveat.arable
      : family === "grass"
        ? canopyGoneOnBareSoil(kind, zone)
          ? T.verdict.caveat.grassTilled
          : T.verdict.caveat.grass
        : zone === "bare"
          ? T.verdict.caveat.unknownUseTilled
          : T.verdict.caveat.unknownUse;
  // У схода без даты незнание ещё и про срок: сколько поле стоит таким, по снимкам не сказать.
  return kind === "maybe" ? `${base} ${T.verdict.caveat.noDate}` : base;
};

const capitalise = (text: string): string =>
  text.length === 0 ? text : text[0]!.toUpperCase() + text.slice(1);

const rejectionCounts = (audit: AuditRow[]): Map<string, number> => {
  const counts = new Map<string, number>();
  for (const row of audit) {
    if (row.accepted) continue;
    const reason = (row.rejection_reason || "UNKNOWN").split(";")[0]!.trim() || "UNKNOWN";
    counts.set(reason, (counts.get(reason) ?? 0) + 1);
  }
  return counts;
};

const mean = (values: number[]): number | null =>
  values.length === 0 ? null : values.reduce((sum, value) => sum + value, 0) / values.length;

/** Why there is no answer. First matching cause wins; the order is cheapest-to-explain. */
export const noDataReason = (run: Run): string => {
  // Причина отказа адресуется по КОДУ, а не пересказом английского сообщения сервера:
  // `error_reason` уже приходит и уже разобран, а сам английский текст живёт на ярусе 3
  // (`T.dev.runError`) целиком и без обрезки (правила текстов — ТЗ.md).
  if (run.status === "failed") {
    return T.reason.byCode(run.error_reason);
  }

  const payload = run.payload;
  // The server aggregated the audit into one honest sentence — снег / облачность / нет
  // пролётов, with the counts behind it — and it can do it better than this function can:
  // it sees the rejected acquisitions, their snow fractions and the dates they belong to.
  // Everything below stays as the fallback for runs stored before that existed.
  const obstruction = run.result?.obstruction_ru ?? payload?.obstruction?.note_ru;
  if (obstruction) return capitalise(obstruction);

  const audit = payload?.optical_audit ?? [];
  const accepted = audit.filter((row) => row.accepted).length;

  if (audit.length > 0 && accepted === 0) {
    const counts = rejectionCounts(audit);
    const dominant = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
    const badPercents = audit
      .map((row) => row.bad_percent)
      .filter((value): value is number => isFiniteNumber(value));
    if (dominant === "NO_FIELD_PIXELS") return T.reason.tooSmall;
    if (dominant === "LOCAL_BAD_PIXELS_ABOVE_LIMIT" || dominant === "VALID_COVERAGE_BELOW_LIMIT") {
      const average = mean(badPercents);
      // Счёта отклонённых в причине больше нет: знаменатель на экране один, и печатает его
      // блок снимков (`strip.summary`, UX_SPEC §3.5).
      return average === null
        ? T.reason.noAcceptedDates
        : `${T.reason.noAcceptedDates} ${T.reason.clouds(Math.round(average))}`;
    }
    return T.reason.noAcceptedDates;
  }

  const snowPercents = audit
    .filter((row) => row.accepted)
    .map((row) => row.snow_percent)
    .filter((value): value is number => isFiniteNumber(value));
  const snow = mean(snowPercents);
  if (snow !== null && snow >= DISPLAY_SNOW_DOMINANT_FRACTION * 100) {
    return T.reason.snow(Math.round(snow));
  }

  const age = run.result?.optical_age_days;
  if (isFiniteNumber(age) && age > PIPELINE_OPTICAL_STALE_DAYS) return T.reason.stale(age);

  // `radar_skip_reason` НЕ приклеивается сюда: оно объясняет ОТСУТСТВИЕ радара человеку, у
  // которого радар нигде не участвовал, — бриф это прямо запрещает. Причина остаётся
  // дословно на ярусе 3 (`T.dev.radarSkip`, DevPanel).
  return T.reason.noEvent;
};

/**
 * Заголовок молчания выбирается ПО ПРИЧИНЕ, а не по состоянию (heading-noshots-clean):
 * фолбэк `reason.noEvent` срабатывает и при дюжине чистых снимков, и «Нет чистых снимков
 * спутника» стояло ровно над причиной «Чистые снимки есть…» — две соседние строки спорили.
 * Сравнение по тождеству строки словаря сознательное: заголовок и причина берутся из ОДНОГО
 * `noDataReason`, поэтому разойтись они не могут по построению. У упавшего прогона заголовок
 * свой (`run.failed`) — сюда он не попадает.
 */
export const noDataHeading = (run: Run): string =>
  run.status !== "failed" && noDataReason(run) === T.reason.noEvent
    ? T.verdict.heading.noAnswer
    : T.verdict.heading.noShots;

/**
 * Тезис состояния — фраза о ПОЛЕ, а не о нашей метаинформации.
 *
 * Глагол один на все датированные состояния: «растительность сошла». Продукт ловит сход, а
 * не «уборку» (защёлка требует пик NDVI ≥ 0.65 до события), а слово «голое» закреплено за
 * состоянием почвы в блоке слежения и на карточке ответа не встречается.
 */
const humanSentence = (
  kind: VerdictKind,
  family: FieldUseFamily,
  zone: SurfaceZone,
  from: string | null,
  to: string | null,
  latest: string | null,
): string => {
  // У шестого состояния факта нет вовсе: причина И ЕСТЬ единственное содержательное
  // предложение карточки, и печатать её дважды значит нарушить правило одного отрицания
  // (UX_SPEC §3.2). Пустая строка — сигнал карточке не рисовать слот.
  if (kind === "nodata") return "";
  // What was measured, in the state's own words. Not a harvest verdict and it says so.
  if (kind === "fallow") return T.verdict.fallowPlain;
  // An observation and nothing more, WITH the date it was made on: "было вчера" and
  // "было 40 дней назад" are the difference between "не езжай" and "мы не знаем". NDVI is
  // not printed here — the number lives in the disclosure (правила текстов — ТЗ.md).
  if (kind === "crop") {
    return latest === null ? T.verdict.cropPlain : T.verdict.cropOn(formatDayMonth(latest));
  }
  // Приписка ЛУГА. Заголовок №4 один и тот же у стоящей травы и у схода, поэтому сказать,
  // что именно произошло, обязан факт — и складывается приписка со всеми четырьмя вариантами
  // фразы про сход, а не заменяет их (правила текстов — ТЗ.md).
  //
  // НО ТОЛЬКО ПОД ЗАГОЛОВКОМ №4. На паре «трава × открытая почва» заголовок больше не
  // травяной — его выбрала зона, — и приписка «поле остаётся под многолетними травами»
  // спорила бы с ним в одной карточке: тезис утверждает, что поле осталось лугом, заголовок
  // над ним — что почву обработали. Условие то же, что сняло перехват, и зовётся тем же
  // предикатом: три голоса об одном поле не имеют права расходиться.
  const tail =
    family === "grass" && !canopyGoneOnBareSoil(kind, zone) ? ` ${T.verdict.eventGrassTail}` : "";

  // Что случилось с полем, а не «дата события не определена». Бэкенд эту фразу уже писал
  // (final_detector.py, поле `why`); интерфейс печатал вместо неё свою метаинформацию.
  if (kind === "maybe") return `${T.verdict.canopyGone}${tail}`;
  if (kind === "radar") {
    if (!from || !to) return `${T.verdict.eventUnknown}${tail}`;
    return from === to
      ? `${T.verdict.eventByRadarSingle(formatDayMonth(from))}${tail}`
      : `${T.verdict.eventByRadar(formatDayMonthSpan(from, to))}${tail}`;
  }
  if (from && to && from !== to) {
    return `${T.verdict.eventBetween(formatDayMonthSpan(from, to))}${tail}`;
  }
  const single = from ?? to;
  return single
    ? `${T.verdict.eventSingle(formatDayMonth(single))}${tail}`
    : `${T.verdict.eventUnknown}${tail}`;
};

// `fallowSentence` / `fallowEvidence` УДАЛЕНЫ 2026-09-05 (закон зачистки,
// (правила текстов — ТЗ.md): пересказ границы Уилсона — внутренняя методика, продукт её
// не носит. Счёт чистых снимков окна печатает `fallowSpan` (строка 4 состояния `fallow`).

/** Приглашение вернуться: разное по причине, потому что ждут разного и разные сроки. */
const comebackFor = (code: Obstruction["code"] | null): string => {
  if (code === "SNOW") return T.verdict.comeBackSnow;
  if (code === "CLOUD" || code === "UNREADABLE") return T.verdict.comeBackCloud;
  if (code === "NO_ACQUISITIONS") return T.verdict.comeBackNoAcquisitions;
  return T.verdict.comeBack;
};

/**
 * Характер схода — НАБЛЮДЕНИЕ: как выглядела перемена на снимках. Число, из которого оно
 * читается, на продуктовую поверхность не выходит — оно равно шагу сигнала, делённому на
 * его же порог. Граница между двумя формулировками (`DISPLAY_SHARP_DROP_SCORE`) — только
 * слова: ни одно решение, ни один цвет и ни одно состояние от неё не зависят.
 *
 * Остальные состояния строки не получают (drop-lines-off-card): семейство `changeUnknown*`
 * дублировало `canopyGoneNoDate` и `radarLine`, а у nodata ветка была мёртвой.
 */
const dropLineFor = (kind: VerdictKind, score: number | null): string | null => {
  if (kind !== "free") return null;
  return score !== null && score >= DISPLAY_SHARP_DROP_SCORE
    ? T.verdict.dropSharp
    : T.verdict.dropGradual;
};

/** «Что видно на снимках»: наблюдение под тезисом. Null там, где видно ничего не было. */
const measuredFor = (kind: VerdictKind): string | null => {
  // Признак, по которому событие найдено, здесь НЕ называется: канал, его поведение и
  // величина пика до события — это правило, а не то, что видно (правила текстов — ТЗ.md).
  if (kind === "free" || kind === "radar") return T.verdict.measuredEvent;
  if (kind === "crop") return T.verdict.cropCaption;
  // У пара раскрытия больше НЕТ: перечисление режимов отказа стало видимой оговоркой
  // (`caveat.fallow`). Под заголовком, который утверждает пар, оно не может ждать клика.
  return null;
};

export const verdictFromRun = (
  run: Run | null | undefined,
  use: FieldUseCode | null | undefined = null,
): Verdict | null => {
  if (!run) return null;
  if (run.status === "running") return null;

  const result = run.result;
  const decision = result?.decision;
  const kind: VerdictKind =
    run.status === "failed" || !decision ? "nodata" : KIND_BY_DECISION[decision];
  const family = fieldUseFamily(use);
  // Упавший прогон и закрытое небо — РАЗНЫЕ вещи, и заголовок обязан их различать: там
  // ответа не будет по погоде, здесь его не посчитали (UX_SPEC §5, S6).
  const failed = run.status === "failed";

  const eventFrom = result?.event_from ?? run.payload?.result?.harvest_date_from ?? null;
  const eventTo = result?.event_to ?? run.payload?.result?.harvest_date_to ?? null;
  const dryDays = result?.dry_days ?? null;
  const drySince = result?.dry_since ?? null;
  const accepted = result?.observations_accepted;
  const total = result?.observations_total;
  const latestClearDate = result?.latest_optical_date ?? null;
  const obstructionCode = run.payload?.obstruction?.code ?? null;
  const bare = result?.bare ?? null;
  const score = isFiniteNumber(result?.score_0_100) ? result!.score_0_100 : null;

  const dryLine =
    drySince && isFiniteNumber(dryDays)
      ? // Вторая дата — день, К КОТОРОМУ считали. Без неё строка читалась «прошло 27 дней»,
        // то есть к сегодняшнему дню, и анализ недельной давности удлинял срок на свой
        // возраст (dry-anchor).
        T.verdict.dryConfirmed(formatDayMonth(drySince), formatDayMonth(run.analysis_date), dryDays)
      : kind === "nodata" || kind === "crop" || kind === "fallow" || kind === "radar"
        ? null
        : T.verdict.dryNotConfirmed;

  // Строка 4, одна на состояние. Порядок ветвления — порядок состояний в брифе.
  const secondLine =
    kind === "free"
      ? dryLine
      : kind === "radar"
        ? latestClearDate
          ? T.verdict.radarLine(formatDayMonth(latestClearDate))
          : T.verdict.radarLineNoDate
        : kind === "maybe"
          ? T.verdict.canopyGoneNoDate
          : kind === "crop"
            ? T.verdict.cropUnknownAfter
            : kind === "fallow"
              ? bare
                ? T.verdict.fallowSpan(
                    bare.clear_dates,
                    formatDayMonth(bare.first_date),
                    formatDayMonth(bare.last_date),
                  )
                : null
              : // У молчания строки 4 нет: приглашение вернуться живёт в своём слоте
                // (`comeBack`), потому что это совет, а не то, что делает тезис утверждением.
                null;

  // Поверхность на последнем чистом снимке — то, от чего теперь берётся тон. Строку про неё
  // печатает блок доказательства (`LevelBar`), и печатает без «Чисел»: тон обязан быть
  // объяснён видимым текстом, а раскрытие можно не открыть.
  const zone = surfaceZoneOf(run);
  // Отказ прогона — не запрет и не разрешение: `kind` у него и так `nodata`, но записано это
  // отдельно, потому что заголовок у отказа свой и на светофоре он обязан молчать.
  const tone: VerdictTone = failed ? "unknown" : verdictTone(kind, family, zone);

  // Причина считается ДО заголовка: у молчания заголовок выбирается по ней
  // (heading-noshots-clean), и оба обязаны стоять на одном `noDataReason`.
  const reason = kind === "nodata" ? noDataReason(run) : null;
  const inconclusive = !failed && reason === T.reason.noEvent;

  return {
    kind,
    failed,
    label: failed
      ? T.run.failed
      : kind === "nodata"
        ? inconclusive
          ? T.verdict.heading.noAnswer
          : T.verdict.heading.noShots
        : verdictHeading(kind, family, zone),
    zone,
    tone,
    colorVar: VERDICT_TONE_COLOR_VAR[tone],
    score,
    human: humanSentence(kind, family, zone, eventFrom, eventTo, latestClearDate),
    secondLine,
    // Упавший прогон приглашения не получает: ответ не посчитан, а не закрыт небом, и
    // «когда сойдёт снег» объясняло бы чужую причину (UX_SPEC §5, S6). При чистых снимках
    // без ответа приглашение своё: «когда появятся чистые снимки» звало бы ждать того,
    // что уже есть (heading-noshots-clean).
    comeBack:
      kind === "nodata" && !failed
        ? inconclusive
          ? T.verdict.comeBackNoAnswer
          : comebackFor(obstructionCode)
        : null,
    dropLine: dropLineFor(kind, score),
    // ЕДИНСТВЕННЫЙ случай, когда о поверхности говорит карточка: жёлтый выпал ИЗ-ЗА того, что
    // зоны нет, — значит, полоски уровня на экране нет тоже, и объяснить цвет больше нечем.
    // Условие записано через сам тон, а не через список состояний: под запретом и под
    // молчанием зона не спрашивается вовсе, и строка про неё была бы там ответом на
    // незаданный вопрос. Двух голосов о поверхности не возникает никогда — случаи
    // взаимоисключающие: есть зона — говорит полоска, нет зоны — говорит эта строка.
    surfaceLine: tone === "caution" && zone === "unknown" ? T.verdict.surfaceUnknown : null,
    measured: measuredFor(kind),
    reason,
    eventFrom,
    eventTo,
    latestClearDate,
    obstructionCode,
    // "Одна чистая дата из пятнадцати" is the difference between a closed sky and a rule
    // that had everything it needed and still said nothing. It used to live only in the
    // database (backend `_observation_qa`).
    qa: isFiniteNumber(accepted) && isFiniteNumber(total) ? { accepted, total } : null,
    // Свежесть съёмки теперь стоит В ЭТОЙ ЖЕ строке QA у вердикта (`qaCountsFresh`), а не
    // отдельной строкой в части 2: правило «показывать дважды значит выдавать одно
    // наблюдение за два» этим исполняется, а не отменяется — после разреза поверхностей
    // «дважды» превратилось бы в «ни разу», а свежесть это половина ответа.
    // The negation stays for "Освободилось": "голое, но сухость не подтверждена" is
    // operationally different from "голое и сухо с 15.10".
    dryLine,
    warnings: (result?.warnings ?? run.payload?.result?.warnings ?? []).filter(
      (value): value is string => typeof value === "string",
    ),
    // Both states that ask for a human: ВОЗМОЖНО, and a radar answer. The radar interval
    // misses the labelled harvest in roughly one case in seven against 0 in 40 for the
    // optical one, so "смотрим снимок глазами" is the honest instruction there too.
    needsEyes: kind === "maybe" || kind === "radar",
    // What optics alone said, when radar answered instead. Hiding it would hide the
    // contradiction: on the clear dates before the gap the canopy was often still standing.
    opticalAnswer:
      kind === "radar"
        ? verdictHeading(verdictKindOfDecision(run.payload?.optical_answer?.decision), family, zone)
        : null,
    runId: run.id,
    analysisDate: run.analysis_date,
  };
};
